import React, { useState, useEffect } from "react";
import { calculateEligibility, parseNumber, formatCurrency, clamp } from "../utils/calculatorLogic";
import RangeSlider from "./common/RangeSlider";
import { generatePdf } from "../utils/pdf/generatePdf";
import { sharePdf } from "../utils/pdf/sharePdf";
import { mapEligibilityCalculatorData } from "../utils/pdf/pdfMapper";
import { createPdfCacheManager } from "../utils/pdf/pdfCacheManager";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ReadMoreText from "./ReadMoreText";
ChartJS.register(ArcElement, Tooltip, Legend);

function EligibilityCalculator({ showHeader, headerTitle, headerDesc }) {
  const [monthlyIncome, setMonthlyIncome] = useState(50000);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isSharingPdf, setIsSharingPdf] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [otherEmi, setOtherEmi] = useState(10000);
  const [years, setYears] = useState(10);
  const [interest, setInterest] = useState(10);
  const totalTenureMonths = years * 12;
  const APPLY_URL = "/property-loan/apply";
  const DEFAULT_HEADER_TITLE = "Unlock New Possibilities";
  const DEFAULT_HEADER_DESC = "Estimate how much loan you can get based on your income and financial details. Plan your home purchase with confidence.";
  const shouldShowHeader = showHeader?.toLowerCase() === "true";
  const resolvedHeaderTitle = headerTitle?.trim() || DEFAULT_HEADER_TITLE;
  const resolvedHeaderDesc = headerDesc?.trim() || DEFAULT_HEADER_DESC;

  const { loanEligibility, maxEmi } = calculateEligibility(monthlyIncome,otherEmi,totalTenureMonths,interest);
  const eligibleAmount = Math.round(loanEligibility);
  const maxEmiAllowed = Math.round(maxEmi);

  const chartData = {
    labels: ["Eligible Loan", "Remaining Capacity"],
    datasets: [
      {
        data: [eligibleAmount,Math.max(0, monthlyIncome * totalTenureMonths - eligibleAmount),],
         backgroundColor: ["#5563c1", "#e04644"],
        borderWidth: 0,
        cutout: "70%",
        hoverBorderWidth: 2,
        hoverBorderColor: ["#5168C1", "#E04848"],
        hoverOffset: 0,
        borderRadius: 0,
        spacing: 0
      },
    ],
  };

  const chartOptions = {
    rotation: -90,
    circumference: 180,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
  enabled: false, // ❗ disable default
  external: function (context) {
    let tooltipEl = document.getElementById("glass-tooltip");

    // create element
    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = "glass-tooltip";
      tooltipEl.innerHTML = `<div class="glass-tooltip-inner"></div>`;
      document.body.appendChild(tooltipEl);
    }

    const tooltip = context.tooltip;

    // hide
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    const data = tooltip.dataPoints?.[0];
    if (!data) return;

    // set content
    tooltipEl.querySelector(".glass-tooltip-inner").innerHTML = `
      <div class="tooltip-title">${data.label}</div>
      <div class="tooltip-value">₹ ${data.raw.toLocaleString()}</div>
    `;

    // position
    const rect = context.chart.canvas.getBoundingClientRect();

tooltipEl.style.opacity = 1;
tooltipEl.style.position = "absolute";
tooltipEl.style.left =
  rect.left + window.scrollX + tooltip.caretX + "px";
tooltipEl.style.top =
  rect.top + window.scrollY + tooltip.caretY + "px";
tooltipEl.style.transform = "translate(-50%, -100%)";
tooltipEl.style.pointerEvents = "none";
  }
},
    },
  };
  const pdfManager = React.useRef(createPdfCacheManager({pdfType: "eligibility",generatePdf,debounceMs: 400,})).current;

  const updateMonthlyIncome = (val) => { pdfManager.markInteracted(); setMonthlyIncome(val); };
  const updateYears = (val) => { pdfManager.markInteracted(); setYears(val); };
  const updateInterest = (val) => { pdfManager.markInteracted(); setInterest(val); };
  const updateOtherEmi = (val) => { pdfManager.markInteracted(); setOtherEmi(val); };

  useEffect(() => {
    const mappedData = mapEligibilityCalculatorData({monthlyIncome,otherEmi,years,interest,loanEligibility: eligibleAmount,maxEmi: maxEmiAllowed,});
    setPdfData(mappedData);
    pdfManager.scheduleCache(mappedData);
  }, [monthlyIncome,otherEmi,years,interest,eligibleAmount,maxEmiAllowed]);

const shadowPlugin = {
  id: "shadowPlugin",
  beforeDraw(chart) {
    const { ctx } = chart;

    ctx.save();

    ctx.shadowColor = "rgba(0,0,0,0.32)"; // #00000052
    ctx.shadowBlur = 7.5;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 5;
  },
  afterDraw(chart) {
    chart.ctx.restore();
  }
};
const hoverGlowPlugin = {
  id: "hoverGlowPlugin",
  afterDatasetDraw(chart, args) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);

    meta.data.forEach((arc, index) => {
      if (arc.active && index === 0) { // only blue part
        ctx.save();

        ctx.shadowColor = "#5168C1";
        ctx.shadowBlur = 15;

        arc.draw(ctx);

        ctx.restore();
      }
    });
  }
};
    const handlePdfDownload = async () => {
      if (!pdfData) return;
      setIsDownloadingPdf(true);
      try {
        const blob = await pdfManager.getBlob(pdfData);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Home_Loan_Eligibility_Details.pdf";
        a.click();
        URL.revokeObjectURL(url);
      } finally {
        setIsDownloadingPdf(false);
      }
    };

    const handlePdfShare = async () => {
      if (!pdfData) return;
      setIsSharingPdf(true);
      try {
        const blob = await pdfManager.getBlob(pdfData);
        await sharePdf(blob, "Home_Loan_Eligibility_Details.pdf");
      } finally {
        setIsSharingPdf(false);
      }
    };

  return (
  <div className="calc mt-4">
  {shouldShowHeader && (
             <div className="paradescwrap mobile-only-header">
               <p className="title large-body bold">{resolvedHeaderTitle}</p>
               <div className="description">
                 <p className="description-text regular-body regular neutral800">
                   <ReadMoreText text={resolvedHeaderDesc} limit={200} />
                 </p>
               </div>
             </div>
           )}
    <div  class="wrapthechart">
      <div class="chartwrapperformobile">
      <div className="chart-wrapper-mobile">
              <Doughnut data={chartData} options={chartOptions} plugins={[shadowPlugin, hoverGlowPlugin]}/>

              <div className="chart-center">
                <p class="your-emi">Your Eligibility</p>
                <p className="currency">₹ {formatCurrency(eligibleAmount)}</p>
                <span>Maximum Loan</span>
              </div>
            </div>
            <div class="donughtmobile">
            <div className="legend-mobile d-flex">
              <div className="legend-item"><span className="dot principal-dot"></span><span className="principal font-w-700 font-14 neutral800">Eligible Loan</span></div>
              <div className="legend-item"><span className="dot interest-dot"></span><span className="font-w-700 font-14 neutral800 interest">Remaining Capacity</span></div>
            </div>
            </div>
        </div>
    <div className="emi-container max-w-desktop">
      <div className="emi-layout">
        <div className="emi-left">
           {shouldShowHeader && (
             <div className="paradescwrap">
               <p className="title large-body bold">{resolvedHeaderTitle}</p>
               <div className="description">
                 <p className="description-text regular-body regular neutral800">
                   <ReadMoreText text={resolvedHeaderDesc} limit={200} />
                 </p>
               </div>
             </div>
           )}

          <div className="input-group">
            <div className="input-header">
              <label>Monthly Gross Income</label>
              <div className="input-box">
                <span>₹</span>
                <input type="text" value={formatCurrency(monthlyIncome)} min="5000" max="1000000" step="1000" onChange={(e) => updateMonthlyIncome(clamp(parseNumber(e.target.value), 0, 1000000))}/>
              </div>
            </div>
             <RangeSlider
               min={20000} max={1000000} step={1000} value={monthlyIncome} onChange={(e) => updateMonthlyIncome(clamp(Number(e.target.value), 20000, 1000000))}
               formatValue={(val) => val >= 100000 ? `₹ ${(val / 100000).toFixed(1)} L` : `₹ ${(val / 1000).toFixed(0)} K`}
               marks={[
                    { value: 20000, label: "20K" },
                    { value: 25000, label: "25K" },
                    { value: 50000, label: "50K" },
                    { value: 100000, label: "1L" },
                    { value: 500000, label: "5L" },
                    { value: 1000000, label: "10L" }
                    ]}
             />
          </div>

          <div className="input-group">
            <div className="input-header">
              <label>Term (Years)</label>
              <div className="input-box">
                <span>Y</span>
                <input type="number" value={years} min="1" max="25" onChange={(e) => updateYears(clamp(Number(e.target.value), 1, 25))}/>
              </div>
            </div>
            <RangeSlider
              min={1} max={25} step={1} value={years} onChange={(e) => updateYears(clamp(Number(e.target.value), 1, 25))}
              formatValue={(val) => `${val} Years`}
              marks={[
                { value: 1, label: "1Y" },
                { value: 5, label: "5Y" },
                { value: 10, label: "10Y" },
                { value: 15, label: "15Y" },
                { value: 20, label: "20Y" },
                { value: 25, label: "25Y" }
              ]}
            />
          </div>

          <div className="input-group">
            <div className="input-header">
              <label>Interest Rate (p.a.)</label>
              <div className="input-box">
                <span>%</span>
                <input type="number" value={interest} min="6" max="22" step="0.1" onChange={(e) => updateInterest(clamp(Number(e.target.value), 1, 22))}/>
              </div>
            </div>
            <RangeSlider
              min={6} max={22} step={0.1} value={Math.max(interest, 6)} onChange={(e) => updateInterest(clamp(Number(e.target.value), 6, 22))}
              formatValue={(val) => `${val} %`}
              marks={[
                { value: 6, label: "6%" },
                { value: 10, label: "10%" },
                { value: 14, label: "14%" },
                { value: 18, label: "18%" },
                { value: 22, label: "22%" }
              ]}
            />
          </div>

          <div className="input-group">
            <div className="input-header">
              <label>Other EMIs</label>
              <div className="input-box">
                <span>₹</span>
                <input type="text" value={formatCurrency(otherEmi)} min="0" max="300000" step="1000" onChange={(e) => updateOtherEmi(clamp(parseNumber(e.target.value), 0, 300000))}/>
              </div>
            </div>
            <RangeSlider
              min={0} max={300000} step={1000} value={otherEmi} onChange={(e) => updateOtherEmi(clamp(Number(e.target.value), 0, 300000))}
              formatValue={(val) => val >= 100000 ? `₹ ${(val / 100000).toFixed(0)} L` : `₹ ${(val / 1000).toFixed(0)} K`}
              marks={[
                { value: 0, label: "0" },
                { value: 80000, label: "80k" },
                { value: 150000, label: "1.5L" },
                { value: 220000, label: "2.2L" },
                { value: 300000, label: "3L" }
              ]}
            />
          </div>
        </div>
        <div className="emi-right">
           <div class="emibg">
          <div className="emi-curvature">
            <div className="chart-wrapper">
              <Doughnut data={chartData} options={chartOptions} />

              <div className="chart-center">
                <p class="your-emi">Your Eligibility</p>
                <p className="currency">₹ {formatCurrency(eligibleAmount)}</p>
                <span>Maximum Loan</span>
              </div>
            </div>

            <div className="legend">
              <div className="legend-item"><span className="dot principal-dot"></span><span className="principal font-w-700 font-14 neutral800">Eligible Loan</span></div>
              <div className="legend-item"><span className="dot interest-dot"></span><span className="font-w-700 font-14 neutral800 interest">Remaining Capacity</span></div>
            </div>

            <div className="summary">
              <div className="summary-row">
                <span className="font-w-500 font-14 neutral600 prin">Monthly Income</span>
                <span className="rupee font-w-700 font-14 neutral1000">₹<span className="numericvalue rupee font-w-500 font-14 neutral1000">{formatCurrency(monthlyIncome)}</span></span>
              </div>
              <div className="summary-row">
                <span  className="font-w-500 font-14 neutral600 interesttotal">Other EMIs</span>
                <span className="rupee font-w-700 font-14 neutral1000">₹<span className="numericvaluerupee font-w-500 font-14 neutral1000"> {formatCurrency(otherEmi)}</span></span>
              </div>
              <div className="summary-row">
                <span className="font-w-500 font-14 neutral600">Max EMI Allowed</span>
              <span className="rupee font-w-700 font-14 neutral1000">₹ <span className="numericvalue rupee font-w-500 font-14 neutral1000">{formatCurrency(maxEmiAllowed)}</span></span>
              </div>
            </div>

            <div className="card-actions">
              <div className="icon-group">
                <button className="icon-btn" onClick={handlePdfShare} disabled={isSharingPdf}>
                    <img src={`/documents/d/global/share-icon`} alt="Share"/>
                    <span className="caption-body neutral800">
                      {isSharingPdf ? "Please wait.." : "Share"}
                    </span>
                </button>
                <button className="icon-btn" onClick={handlePdfDownload} disabled={isDownloadingPdf}>
                    <img src={`/documents/d/global/download-icon`} alt="Download"/>
                    <span className="caption-body neutral800">
                      {isDownloadingPdf ? "Please wait.." : "Download"}
                    </span>
                </button>
              </div>
            </div>
          </div>
          </div>
          <div className="left-actions other-calculator">
            <button className="apply-btn btn primary-button" onClick={ () => { globalThis.location.href = APPLY_URL;}}>Apply Now</button>
            <button className="callback-btn shimmer-button" onClick={() => globalThis.openApplyModal?.()}>
              <span className="shimmer-text">Get a call back</span>
              <svg className="shimmer-border" viewBox="0 0 232 48" preserveAspectRatio="none">
                <rect x="1" y="1" width="230" height="46" rx="24" ry="24" pathLength="1"></rect>
              </svg>
            </button>
      </div>
        </div>
      </div>
    </div>
    </div>
	</div>
  );
}


export default EligibilityCalculator;
